import { isAudit as checkIsAudit, isEngagement, isRole } from "PFApp/activities/helpers";
import { TemplateKey } from "PFApp/constants/templates";
import { useTemplateFind } from "PFApp/hooks";
import { useActivityFormDefaultAttributes } from "PFApp/hooks/activity_form/activity_form_default_attributes/use_activity_form_default_attributes";
import { useActivity } from "PFCore/hooks/queries/activity";
import { useAttachments } from "PFCore/hooks/queries/attachments/use_attachments";
import { useInterestQuestionnaires } from "PFCore/hooks/queries/common/use_interest_questionnaires";
import { useSubtemplates } from "PFCore/hooks/queries/subtemplates/use_subtemplates";
import { Activity, GIdTargetType } from "PFTypes";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";

import { useParentActivityRolesIds } from "./use_parent_activity_roles_ids";

export const useAsyncStoreInitializer = ({ attributes, isClone, id, type }) => {
  const { t } = useTranslation();
  const findTemplate = useTemplateFind();
  const [searchParams] = useSearchParams();
  const parentActivityIdFromUrl = searchParams.get("parentActivityId");

  const isNew = !id && !!type;
  const isEdit = !isNew;

  const {
    data: activity,
    isError: isActivityError,
    isFetched: isActivityFetched
  } = useActivity(id!, { enabled: !!id });
  const { isFetched: areInterestQuestionnairesFetched } = useInterestQuestionnaires();

  const { data: attachments, isFetched: isAttachmentsFetched } = useAttachments(
    { targetType: GIdTargetType.Task, targetId: id! },
    { enabled: isEdit }
  );

  const parentId = activity?.parent_activity_id || parentActivityIdFromUrl || attributes?.parent_activity_id;

  const { data: parentActivityData, isFetched: isParentActivityFetched } = useActivity(parentId!, {
    enabled: !!parentId
  });

  const parentActivity = !(isClone && parentActivityData?.state === "complete")
    ? parentActivityData
    : undefined;

  const template = findTemplate({
    read_permitted: true,
    hidden: false,
    ...(isNew ? { key: type } : { id: activity?.template_id })
  });

  const isAudit = checkIsAudit(template);
  const { isFetching: isFetchingParentActivityRoles } = useParentActivityRolesIds({
    parentActivityId: parentId ? Number(parentId) : null
  });

  const { data: subtemplates, isFetched: isSubtemplatesFetched } = useSubtemplates(
    { templateKey: template?.key as TemplateKey },
    { enabled: !!template?.key }
  );

  const { getActivityFormDefaultAttributes } = useActivityFormDefaultAttributes({
    template: template!,
    parentActivity
  });

  const activityToEdit = useMemo(() => {
    const subtemplate =
      (subtemplates || []).find(({ subtemplate_key }) => subtemplate_key === attributes?.subtemplate_key) ||
      null;
    return isNew
      ? getActivityFormDefaultAttributes({
          isClone,
          attributes,
          activityCreator: null,
          subtemplate
        })
      : activity;
  }, [activity, attributes, getActivityFormDefaultAttributes, isClone, isNew, subtemplates]);

  const activityStatus = {
    isNew,
    isEdit,
    isClone,
    isDraft: activityToEdit?.state === "draft",
    isRole: isRole(template),
    isEngagement: isEngagement(template),
    isAudit
  };

  const activityCreator = useMemo(
    () => (isNew ? null : (activityToEdit?.profile ?? null)),
    [isNew, activityToEdit]
  );

  return {
    template: template!,
    activity: activityToEdit as Activity,
    activityStatus,
    activityCreator,
    parentActivity,
    attachments: attachments?.entries || [],
    subtemplates: subtemplates || [],
    errors: isActivityError ? [t("thereWasAnError")] : null,
    loaded:
      (isNew || isActivityFetched) &&
      (parentId ? isParentActivityFetched : true) &&
      (parentId ? !isFetchingParentActivityRoles : true) &&
      ((isNew && !template) || isSubtemplatesFetched) &&
      areInterestQuestionnairesFetched &&
      (isNew || isAttachmentsFetched)
  };
};
